import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Button, TextField, Chip,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox,
  ListItemText, SelectChangeEvent, TablePagination, IconButton,
  Tooltip, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, LinearProgress, Divider
} from '@mui/material';
import {
  Search as SearchIcon, CloudUpload as UploadIcon, Download as DownloadIcon,
  Visibility as PreviewIcon,
  Delete as DeleteIcon, Clear as ClearIcon, AttachFile as AttachIcon,
  PictureAsPdf as PdfIcon, Image as ImageIcon, InsertDriveFile as FileIcon,
  TableChart as ExcelIcon, Description as WordIcon, FolderZip as ZipIcon,
  Close as CloseIcon, Info as InfoIcon, Refresh as RefreshIcon,
  CalendarMonth as DateIcon, Store as BranchIcon, Warehouse as WarehouseIcon,
  AccountBalanceWallet as CustodyIcon, LocalShipping as SupplierIcon,
  Person as PersonIcon, Tag as TagIcon, Receipt as ReceiptIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import AttachmentPreviewDialog from '../components/common/AttachmentPreviewDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';

// ============================================
interface AttachmentDto {
  attachmentId: number; documentType: string; documentTypeAr: string;
  documentId: number; documentNumber: string;
  fileName: string; originalFileName: string; fileExtension: string;
  contentType: string; fileSize: number; fileSizeDisplay: string;
  description: string | null; tags: string | null;
  branchId: number | null; branchName: string; branchNameAr: string;
  warehouseId: number | null; warehouseName: string;
  custodyId: number | null; custodyDisplay: string;
  uploadedByName: string; uploadedDate: string;
}
interface AttachmentResult {
  attachments: AttachmentDto[]; totalCount: number; page: number; pageSize: number;
  totalPages: number; countByType: Record<string, number>;
  totalSize: number; totalSizeDisplay: string;
}
interface Branch { branchId: number; branchName: string; branchNameAr: string; }
interface Custody { custodyId: number; custodyNumber: string; employeeName: string; employeeNameAr: string; status: string; }
interface Warehouse { warehouseId: number; warehouseName: string; warehouseNameAr: string; }

const DropdownSearch: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => (
  <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
    <TextField size="small" fullWidth placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value)} autoFocus onKeyDown={e => e.stopPropagation()}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
  </Box>
);

const getFileIcon = (ext: string) => {
  const e = ext.toLowerCase();
  if (e === 'pdf') return <PdfIcon sx={{ color: '#e53935' }} />;
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(e)) return <ImageIcon sx={{ color: '#43a047' }} />;
  if (['xls', 'xlsx', 'csv'].includes(e)) return <ExcelIcon sx={{ color: '#2e7d32' }} />;
  if (['doc', 'docx'].includes(e)) return <WordIcon sx={{ color: '#1565c0' }} />;
  if (['zip', 'rar'].includes(e)) return <ZipIcon sx={{ color: '#ff9800' }} />;
  return <FileIcon color="action" />;
};

const docTypes = [
  'PurchaseOrder', 'PurchaseInvoice', 'PurchaseReturn', 'StockIssue',
  'WarehouseTransfer', 'BranchTransfer', 'StockCount', 'Voucher', 'JournalEntry', 'DailySale', 'General'
];
const fileTypes = ['image', 'pdf', 'excel', 'word'];

const translations = {
  en: {
    title: 'Document Archive', subtitle: 'Upload, manage and search document attachments',
    fromDate: 'From Date', toDate: 'To Date', documentType: 'Document Type', allTypes: 'All Types',
    fileType: 'File Type', allFileTypes: 'All Files',
    branches: 'Branches', allBranches: 'All Branches', warehouse: 'Warehouse', allWarehouses: 'All Warehouses',
    custody: 'Custody', allCustodies: 'All Custodies',
    search: 'Search', clear: 'Clear', upload: 'Upload Files', searchInList: 'Search...',
    selectAll: 'Select All', searchPlaceholder: 'Search file name, description, tags...',
    fileName: 'File Name', type: 'Doc Type', size: 'Size', uploadedBy: 'Uploaded By',
    uploadDate: 'Upload Date', branch: 'Branch', actions: 'Actions',
    noData: 'No attachments found', total: 'Total', totalSize: 'Total Size',
    download: 'Download', preview: 'Preview', delete: 'Delete', rowsPerPage: 'Rows per page',
    uploadTitle: 'Upload Attachments', selectDocType: 'Link to Document Type',
    documentId: 'Document Number', description: 'Description', tags: 'Tags (comma separated)',
    selectBranch: 'Branch', selectWarehouse: 'Warehouse', selectCustody: 'Custody',
    selectFiles: 'Select Files', dragDrop: 'or drag and drop here',
    uploading: 'Uploading...', uploadSuccess: 'Files uploaded successfully',
    cancel: 'Cancel', confirmDelete: 'Are you sure you want to delete this attachment?',
    yes: 'Yes', no: 'No', maxSize: 'Max 20 MB per file',
    allowedTypes: 'PDF, Images, Excel, Word, Text, ZIP',
    image: 'Images', pdf: 'PDF', excel: 'Excel', word: 'Word',
    linkedDoc: 'Attachments for', linkedHint: 'Upload files for this document — all fields are pre-filled',
    docInfo: 'Document Info', date: 'Date', status: 'Status', amount: 'Amount',
    supplier: 'Supplier', createdBy: 'Created By', kwd: 'KWD',
    PurchaseOrder: 'Purchase Order', PurchaseInvoice: 'Purchase Invoice', PurchaseReturn: 'Purchase Return',
    StockIssue: 'Stock Issue', WarehouseTransfer: 'Warehouse Transfer', BranchTransfer: 'Branch Transfer',
    StockCount: 'Stock Count', Voucher: 'Voucher', JournalEntry: 'Journal Entry', DailySale: 'Daily Sale', General: 'General',
  },
  ar: {
    title: 'أرشيف المستندات', subtitle: 'رفع وإدارة والبحث في مرفقات المستندات',
    fromDate: 'من تاريخ', toDate: 'إلى تاريخ', documentType: 'نوع المستند', allTypes: 'كل الأنواع',
    fileType: 'نوع الملف', allFileTypes: 'كل الملفات',
    branches: 'الفروع', allBranches: 'كل الفروع', warehouse: 'المخزن', allWarehouses: 'كل المخازن',
    custody: 'العهدة', allCustodies: 'كل العهد',
    search: 'بحث', clear: 'مسح', upload: 'رفع ملفات', searchInList: 'بحث...',
    selectAll: 'اختيار الكل', searchPlaceholder: 'بحث باسم الملف، الوصف، الكلمات المفتاحية...',
    fileName: 'اسم الملف', type: 'نوع المستند', size: 'الحجم', uploadedBy: 'رفع بواسطة',
    uploadDate: 'تاريخ الرفع', branch: 'الفرع', actions: 'إجراءات',
    noData: 'لا توجد مرفقات', total: 'الإجمالي', totalSize: 'الحجم الكلي',
    download: 'تحميل', preview: 'عرض', delete: 'حذف', rowsPerPage: 'صفوف في الصفحة',
    uploadTitle: 'رفع مرفقات', selectDocType: 'ربط بنوع المستند',
    documentId: 'رقم المستند', description: 'الوصف', tags: 'كلمات مفتاحية (مفصولة بفاصلة)',
    selectBranch: 'الفرع', selectWarehouse: 'المخزن', selectCustody: 'العهدة',
    selectFiles: 'اختر الملفات', dragDrop: 'أو اسحب وأفلت هنا',
    uploading: 'جاري الرفع...', uploadSuccess: 'تم رفع الملفات بنجاح',
    cancel: 'إلغاء', confirmDelete: 'هل أنت متأكد من حذف هذا المرفق؟',
    yes: 'نعم', no: 'لا', maxSize: 'الحد الأقصى 20 ميجابايت لكل ملف',
    allowedTypes: 'PDF، صور، Excel، Word، نصوص، ZIP',
    image: 'صور', pdf: 'PDF', excel: 'Excel', word: 'Word',
    linkedDoc: 'مرفقات:', linkedHint: 'ارفع الملفات لهذا المستند — جميع الحقول معبأة تلقائياً',
    docInfo: 'بيانات المستند', date: 'التاريخ', status: 'الحالة', amount: 'المبلغ',
    supplier: 'المورد', createdBy: 'بواسطة', kwd: 'د.ك',
    PurchaseOrder: 'أمر شراء', PurchaseInvoice: 'فاتورة شراء', PurchaseReturn: 'مرتجع شراء',
    StockIssue: 'إذن صرف', WarehouseTransfer: 'تحويل مخزني', BranchTransfer: 'تحويل فروع',
    StockCount: 'جرد', Voucher: 'سند', JournalEntry: 'قيد يومية', DailySale: 'مبيعات يومية', General: 'عام',
  }
};

const DocumentAttachmentsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const isRtl = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ✅ قراءة كل معاملات URL (بيانات المستند القادمة من سجل المعاملات)
  const urlParams = new URLSearchParams(window.location.search);
  const urlDocType = urlParams.get('documentType') || '';
  const urlDocTypeAr = urlParams.get('documentTypeAr') || '';
  const urlDocId = urlParams.get('documentId') || '';
  const urlDocNumber = urlParams.get('documentNumber') || '';
  const urlDocDate = urlParams.get('documentDate') || '';
  const urlStatus = urlParams.get('status') || '';
  const urlTotalAmount = urlParams.get('totalAmount') || '';
  const urlAutoUpload = urlParams.get('autoUpload') === 'true';
  const urlBranchId = urlParams.get('branchId') || '';
  const urlBranchName = urlParams.get('branchName') || '';
  const urlBranchNameAr = urlParams.get('branchNameAr') || '';
  const urlWarehouseId = urlParams.get('warehouseId') || '';
  const urlWarehouseName = urlParams.get('warehouseName') || '';
  const urlWarehouseNameAr = urlParams.get('warehouseNameAr') || '';
  const urlCustodyId = urlParams.get('custodyId') || '';
  const urlCustodyDisplay = urlParams.get('custodyDisplay') || '';
  const urlSupplierName = urlParams.get('supplierName') || '';
  const urlCreatedBy = urlParams.get('createdBy') || '';
  // ✅ وضع الربط: قادم من سجل المعاملات بمستند محدد
  const isLinkedMode = !!(urlDocType && urlDocId && urlDocId !== '0');

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [documentType, setDocumentType] = useState(urlDocType);
  const [fileTypeFilter, setFileTypeFilter] = useState('');
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | ''>('');
  const [custodyId, setCustodyId] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [branchSearch, setBranchSearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [custodySearch, setCustodySearch] = useState('');

  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);

  const [data, setData] = useState<AttachmentResult | null>(null);
  const [loading, setLoading] = useState(false);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadDocType, setUploadDocType] = useState(urlDocType || 'General');
  const [uploadDocId, setUploadDocId] = useState(urlDocId || '0');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadBranch, setUploadBranch] = useState<number | ''>(urlBranchId ? Number(urlBranchId) : '');
  const [uploadWarehouse, setUploadWarehouse] = useState<number | ''>(urlWarehouseId ? Number(urlWarehouseId) : '');
  const [uploadCustody, setUploadCustody] = useState<number | ''>(urlCustodyId ? Number(urlCustodyId) : '');

  // Delete dialog
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [previewAtt, setPreviewAtt] = useState<AttachmentDto | null>(null);

  const filteredBranches = useMemo(() => {
    if (!branchSearch) return branches;
    const s = branchSearch.toLowerCase();
    return branches.filter(b => b.branchName.toLowerCase().includes(s) || b.branchNameAr.includes(s));
  }, [branches, branchSearch]);
  const filteredWarehouses = useMemo(() => {
    if (!warehouseSearch) return warehouses;
    const s = warehouseSearch.toLowerCase();
    return warehouses.filter(w => w.warehouseName.toLowerCase().includes(s) || (w.warehouseNameAr || '').includes(s));
  }, [warehouses, warehouseSearch]);
  const filteredCustodies = useMemo(() => {
    if (!custodySearch) return custodies;
    const s = custodySearch.toLowerCase();
    return custodies.filter(c => c.custodyNumber.toLowerCase().includes(s) || c.employeeName.toLowerCase().includes(s) || (c.employeeNameAr || '').includes(s));
  }, [custodies, custodySearch]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [brRes, whRes, cusRes] = await Promise.allSettled([
          axiosInstance.get('/Branches'), axiosInstance.get('/Warehouses'), axiosInstance.get('/Custodies/lookup'),
        ]);
        if (brRes.status === 'fulfilled') setBranches(brRes.value.data.filter?.((b: any) => b.isActive) || brRes.value.data);
        if (whRes.status === 'fulfilled') setWarehouses(whRes.value.data.filter?.((w: any) => w.isActive !== false) || whRes.value.data);
        if (cusRes.status === 'fulfilled') setCustodies(cusRes.value.data.filter?.((c: any) => c.status === 'Open') || cusRes.value.data);
      } catch { console.error('Failed to fetch lookups'); }
    };
    fetchLookups();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = `page=${page + 1}&pageSize=${pageSize}`;
      if (fromDate) params += `&fromDate=${fromDate}`;
      if (toDate) params += `&toDate=${toDate}`;
      if (documentType) params += `&documentType=${documentType}`;
      if (fileTypeFilter) params += `&fileType=${fileTypeFilter}`;
      if (selectedBranches.length > 0) params += `&branchIds=${selectedBranches.join(',')}`;
      if (warehouseId) params += `&warehouseId=${warehouseId}`;
      if (custodyId) params += `&custodyId=${custodyId}`;
      if (searchText.trim()) params += `&search=${encodeURIComponent(searchText.trim())}`;
      const response = await axiosInstance.get(`/DocumentAttachments?${params}`);
      setData(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error');
    } finally { setLoading(false); }
  }, [fromDate, toDate, documentType, fileTypeFilter, selectedBranches, warehouseId, custodyId, searchText, page, pageSize]);

  useEffect(() => { fetchData(); }, [page, pageSize]);

  // ✅ إذا جاء من سجل المعاملات: جلب مرفقات هذا المستند + فتح الرفع تلقائياً
  useEffect(() => {
    if (isLinkedMode) {
      setDocumentType(urlDocType);
      fetchData();
      if (urlAutoUpload) {
        // تأخير بسيط حتى يتم تحميل الصفحة
        setTimeout(() => setUploadOpen(true), 300);
      }
    }
  }, []);

  const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    if (value.includes(-1)) {
      setSelectedBranches(selectedBranches.length === branches.length ? [] : branches.map(b => b.branchId));
    } else { setSelectedBranches(value); }
  };

  const clearFilters = () => {
    setFromDate(''); setToDate(''); setDocumentType(''); setFileTypeFilter('');
    setSelectedBranches([]); setWarehouseId(''); setCustodyId(''); setSearchText('');
  };

  // ========== رفع ملفات ==========
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setUploadFiles(Array.from(e.target.files));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) setUploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', uploadDocType);
        formData.append('documentId', uploadDocId || '0');
        if (uploadDesc) formData.append('description', uploadDesc);
        if (uploadTags) formData.append('tags', uploadTags);
        if (uploadBranch) formData.append('branchId', String(uploadBranch));
        if (uploadWarehouse) formData.append('warehouseId', String(uploadWarehouse));
        if (uploadCustody) formData.append('custodyId', String(uploadCustody));
        await axiosInstance.post('/DocumentAttachments/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(t.uploadSuccess);
      setUploadOpen(false);
      setUploadFiles([]); setUploadDesc(''); setUploadTags('');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  // ========== تحميل ==========
  const handleDownload = async (id: number, originalName: string) => {
    try {
      const response = await axiosInstance.get(`/DocumentAttachments/download/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = originalName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(isRtl ? 'فشل التحميل' : 'Download failed'); }
  };

  // ========== حذف ==========
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axiosInstance.delete(`/DocumentAttachments/${deleteId}`);
      toast.success(isRtl ? 'تم الحذف بنجاح' : 'Deleted successfully');
      setDeleteId(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  };

  // ✅ اسم المستند المعروض
  const linkedDocLabel = isRtl
    ? `${urlDocTypeAr || (t as any)[urlDocType] || urlDocType} #${urlDocNumber || urlDocId}`
    : `${(t as any)[urlDocType] || urlDocType} #${urlDocNumber || urlDocId}`;

  return (
    <Box sx={{ p: 3 }} dir={isRtl ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
            <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          {!isLinkedMode && (
            <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)} size="large">{t.upload}</Button>
          )}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ✅ بطاقة بيانات المستند المربوط (تظهر فقط عند القدوم من الأرشيف) */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {isLinkedMode && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
          {/* الشريط العلوي */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon color="primary" />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {t.linkedDoc} {linkedDocLabel}
              </Typography>
              {urlStatus && (
                <Chip label={urlStatus} size="small" color={
                  ['Approved', 'Posted', 'Completed'].includes(urlStatus) ? 'success' :
                  ['Pending', 'Draft', 'Open'].includes(urlStatus) ? 'warning' : 'default'
                } />
              )}
            </Box>
            <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)} size="large">
              {t.upload}
            </Button>
          </Box>

          {/* بيانات المستند التفصيلية */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            {urlDocDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DateIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.date}:</Typography>
                <Typography variant="body2" fontWeight="bold">{urlDocDate}</Typography>
              </Box>
            )}
            {urlTotalAmount && Number(urlTotalAmount) > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TagIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.amount}:</Typography>
                <Typography variant="body2" fontWeight="bold">{Number(urlTotalAmount).toFixed(3)} {t.kwd}</Typography>
              </Box>
            )}
            {(urlBranchName || urlBranchNameAr) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BranchIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.branch}:</Typography>
                <Typography variant="body2" fontWeight="bold">{isRtl ? (urlBranchNameAr || urlBranchName) : urlBranchName}</Typography>
              </Box>
            )}
            {(urlWarehouseName || urlWarehouseNameAr) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarehouseIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.warehouse}:</Typography>
                <Typography variant="body2" fontWeight="bold">{isRtl ? (urlWarehouseNameAr || urlWarehouseName) : urlWarehouseName}</Typography>
              </Box>
            )}
            {urlCustodyDisplay && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CustodyIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.custody}:</Typography>
                <Typography variant="body2" fontWeight="bold">{urlCustodyDisplay}</Typography>
              </Box>
            )}
            {urlSupplierName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SupplierIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.supplier}:</Typography>
                <Typography variant="body2" fontWeight="bold">{urlSupplierName}</Typography>
              </Box>
            )}
            {urlCreatedBy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{t.createdBy}:</Typography>
                <Typography variant="body2" fontWeight="bold">{urlCreatedBy}</Typography>
              </Box>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            💡 {t.linkedHint}
          </Typography>
        </Paper>
      )}

      {/* ========== الفلاتر ========== */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.fromDate}
            value={fromDate ? new Date(fromDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setFromDate(newVal);
              if (toDate && newVal && toDate < newVal) setToDate('');
            }}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={toDate ? new Date(toDate) : maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', fullWidth: true, sx: { minWidth: 150 } } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.toDate}
            value={toDate ? new Date(toDate) : null}
            onChange={(date) => {
              const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setToDate(newVal);
              if (fromDate && newVal && fromDate > newVal) setFromDate('');
            }}
            format="dd/MM/yyyy"
            minDate={fromDate ? new Date(fromDate) : minDate}
            maxDate={maxDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={{ textField: { size: 'small', fullWidth: true, sx: { minWidth: 150 } } }}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>{t.documentType}</InputLabel>
          <Select value={documentType} onChange={e => setDocumentType(e.target.value)} label={t.documentType}>
            <MenuItem value="">{t.allTypes}</MenuItem>
            {docTypes.map(dt => <MenuItem key={dt} value={dt}>{(t as any)[dt] || dt}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t.fileType}</InputLabel>
          <Select value={fileTypeFilter} onChange={e => setFileTypeFilter(e.target.value)} label={t.fileType}>
            <MenuItem value="">{t.allFileTypes}</MenuItem>
            {fileTypes.map(ft => <MenuItem key={ft} value={ft}>{(t as any)[ft] || ft}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>{t.branches}</InputLabel>
          <Select multiple value={selectedBranches} onChange={handleBranchChange} input={<OutlinedInput label={t.branches} />}
            onClose={() => setBranchSearch('')} MenuProps={{ PaperProps: { sx: { maxHeight: 350 } }, autoFocus: false }}
            renderValue={sel => sel.length === 0 ? t.allBranches : `${sel.length} ${isRtl ? 'فرع' : 'branches'}`}>
            <DropdownSearch value={branchSearch} onChange={setBranchSearch} placeholder={t.searchInList} />
            <MenuItem value={-1}><Checkbox checked={selectedBranches.length === branches.length && branches.length > 0} /><ListItemText primary={t.selectAll} /></MenuItem>
            {filteredBranches.map(b => <MenuItem key={b.branchId} value={b.branchId}><Checkbox checked={selectedBranches.includes(b.branchId)} /><ListItemText primary={isRtl ? b.branchNameAr : b.branchName} /></MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t.warehouse}</InputLabel>
          <Select value={warehouseId} onChange={e => setWarehouseId(e.target.value as number | '')} label={t.warehouse}
            onClose={() => setWarehouseSearch('')} MenuProps={{ PaperProps: { sx: { maxHeight: 350 } }, autoFocus: false }}>
            <DropdownSearch value={warehouseSearch} onChange={setWarehouseSearch} placeholder={t.searchInList} />
            <MenuItem value="">{t.allWarehouses}</MenuItem>
            {filteredWarehouses.map(w => <MenuItem key={w.warehouseId} value={w.warehouseId}>{isRtl ? (w.warehouseNameAr || w.warehouseName) : w.warehouseName}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t.custody}</InputLabel>
          <Select value={custodyId} onChange={e => setCustodyId(e.target.value as number | '')} label={t.custody}
            onClose={() => setCustodySearch('')} MenuProps={{ PaperProps: { sx: { maxHeight: 350 } }, autoFocus: false }}>
            <DropdownSearch value={custodySearch} onChange={setCustodySearch} placeholder={t.searchInList} />
            <MenuItem value="">{t.allCustodies}</MenuItem>
            {filteredCustodies.map(c => <MenuItem key={c.custodyId} value={c.custodyId}>{c.custodyNumber} - {isRtl ? (c.employeeNameAr || c.employeeName) : c.employeeName}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField size="small" placeholder={t.searchPlaceholder} value={searchText} onChange={e => setSearchText(e.target.value)}
          sx={{ minWidth: 220 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => { setPage(0); fetchData(); }} disabled={loading}>{t.search}</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} size="small">{t.clear}</Button>
      </Paper>

      {/* ========== الملخص ========== */}
      {data && data.totalCount > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${t.total}: ${data.totalCount}`} color="primary" size="small" />
          <Chip label={`${t.totalSize}: ${data.totalSizeDisplay}`} color="info" size="small" />
          {Object.entries(data.countByType).map(([type, count]) => (
            <Chip key={type} label={`${(t as any)[type] || type}: ${count}`} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {/* ========== الجدول ========== */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : data && data.attachments && data.attachments.length > 0 ? (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', whiteSpace: 'nowrap' } }}>
                  <TableCell width={40} /><TableCell>{t.fileName}</TableCell><TableCell>{t.type}</TableCell>
                  <TableCell>{t.size}</TableCell><TableCell>{t.branch}</TableCell>
                  <TableCell>{t.uploadedBy}</TableCell><TableCell>{t.uploadDate}</TableCell>
                  <TableCell align="center">{t.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.attachments.map(att => (
                  <TableRow key={att.attachmentId} hover>
                    <TableCell>{getFileIcon(att.fileExtension)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" component="div" fontWeight="bold" noWrap sx={{ maxWidth: 300 }}>{att.originalFileName}</Typography>
                      {att.description && <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>{att.description}</Typography>}
                    </TableCell>
                    <TableCell><Chip label={isRtl ? att.documentTypeAr : (t as any)[att.documentType] || att.documentType} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{att.fileSizeDisplay}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{isRtl ? att.branchNameAr : att.branchName}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{att.uploadedByName}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{new Date(att.uploadedDate).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t.preview}>
                        <IconButton size="small" color="info" onClick={() => setPreviewAtt(att)}>
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.download}>
                        <IconButton size="small" color="primary" onClick={() => handleDownload(att.attachmentId, att.originalFileName)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.delete}>
                        <IconButton size="small" color="error" onClick={() => setDeleteId(att.attachmentId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={data.totalCount} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize} onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage={t.rowsPerPage} />
        </Paper>
      ) : data ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">{t.noData}</Typography></Paper>
      ) : null}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ✅ Dialog رفع - مع تعبئة تلقائية من المستند المربوط             */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon color="primary" />
            {t.uploadTitle}
            {isLinkedMode && (
              <Chip label={linkedDocLabel} color="primary" size="small" variant="outlined" />
            )}
          </Box>
          <IconButton onClick={() => !uploading && setUploadOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* ✅ ملخص المستند المربوط داخل الـ Dialog */}
          {isLinkedMode && (
            <Paper sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <InfoIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  {isRtl ? 'الرفع مرتبط بـ:' : 'Uploading to:'} {linkedDocLabel}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {urlDocDate && <Typography variant="caption" color="text.secondary">📅 {urlDocDate}</Typography>}
                {(urlBranchName || urlBranchNameAr) && <Typography variant="caption" color="text.secondary">🏪 {isRtl ? (urlBranchNameAr || urlBranchName) : urlBranchName}</Typography>}
                {urlSupplierName && <Typography variant="caption" color="text.secondary">🚛 {urlSupplierName}</Typography>}
                {urlCustodyDisplay && <Typography variant="caption" color="text.secondary">💰 {urlCustodyDisplay}</Typography>}
              </Box>
            </Paper>
          )}

          {/* منطقة السحب والإفلات */}
          <Box sx={{ border: '2px dashed', borderColor: 'primary.main', borderRadius: 2, p: 3, textAlign: 'center', mb: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
            onClick={() => fileInputRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
            <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileSelect} />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography>{t.selectFiles}</Typography>
            <Typography variant="caption" color="text.secondary">{t.dragDrop}</Typography>
            <Typography variant="caption" display="block" color="text.secondary">{t.maxSize} — {t.allowedTypes}</Typography>
          </Box>

          {/* الملفات المحددة */}
          {uploadFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {uploadFiles.map((f, i) => (
                <Chip key={i} label={`${f.name} (${(f.size / 1024).toFixed(0)} KB)`} onDelete={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))}
                  icon={getFileIcon(f.name.split('.').pop() || '') as any} sx={{ m: 0.5 }} variant="outlined" />
              ))}
            </Box>
          )}

          {/* ✅ ربط بمستند - مقفل في وضع الربط */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t.selectDocType}</InputLabel>
              <Select value={uploadDocType} onChange={e => setUploadDocType(e.target.value)} label={t.selectDocType} disabled={isLinkedMode}>
                {docTypes.map(dt => <MenuItem key={dt} value={dt}>{(t as any)[dt] || dt}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" fullWidth
              label={isLinkedMode && urlDocNumber ? `${t.documentId}: ${urlDocNumber}` : t.documentId}
              type="number" value={uploadDocId} onChange={e => setUploadDocId(e.target.value)} disabled={isLinkedMode} />
          </Box>

          <TextField size="small" fullWidth label={t.description} value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} sx={{ mb: 2 }} />
          <TextField size="small" fullWidth label={t.tags} value={uploadTags} onChange={e => setUploadTags(e.target.value)} sx={{ mb: 2 }} />

          {/* ✅ ربط بالفرع/المخزن/العهدة - مقفل في وضع الربط إذا محدد */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t.selectBranch}</InputLabel>
              <Select value={branches.some(b => b.branchId === uploadBranch) ? uploadBranch : ''} onChange={e => setUploadBranch(e.target.value as number | '')} label={t.selectBranch} disabled={isLinkedMode && !!urlBranchId}>
                <MenuItem value="">-</MenuItem>
                {branches.map(b => <MenuItem key={b.branchId} value={b.branchId}>{isRtl ? b.branchNameAr : b.branchName}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t.selectWarehouse}</InputLabel>
              <Select value={warehouses.some(w => w.warehouseId === uploadWarehouse) ? uploadWarehouse : ''} onChange={e => setUploadWarehouse(e.target.value as number | '')} label={t.selectWarehouse} disabled={isLinkedMode && !!urlWarehouseId}>
                <MenuItem value="">-</MenuItem>
                {warehouses.map(w => <MenuItem key={w.warehouseId} value={w.warehouseId}>{isRtl ? (w.warehouseNameAr || w.warehouseName) : w.warehouseName}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t.selectCustody}</InputLabel>
              <Select value={custodies.some(c => c.custodyId === uploadCustody) ? uploadCustody : ''} onChange={e => setUploadCustody(e.target.value as number | '')} label={t.selectCustody} disabled={isLinkedMode && !!urlCustodyId}>
                <MenuItem value="">-</MenuItem>
                {custodies.map(c => <MenuItem key={c.custodyId} value={c.custodyId}>{c.custodyNumber} - {isRtl ? (c.employeeNameAr || c.employeeName) : c.employeeName}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploadFiles.length === 0 || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}>
            {uploading ? t.uploading : `${t.upload} (${uploadFiles.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== Dialog حذف ========== */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>{t.confirmDelete}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>{t.no}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t.yes}</Button>
        </DialogActions>
      </Dialog>

      {previewAtt && (
        <AttachmentPreviewDialog
          open={!!previewAtt}
          onClose={() => setPreviewAtt(null)}
          attachmentId={previewAtt.attachmentId}
          fileName={previewAtt.originalFileName}
          fileExtension={previewAtt.fileExtension}
          isRTL={isRtl}
        />
      )}
    </Box>
  );
};

export default DocumentAttachmentsPage;